<template>
    <div class="stretch-layout">
        <row>
            <column cols="12" lg="8">
                <job-page :job="props.posting" :is-sub-page="props.isSubPage">
                    <template v-if="props.posting.application_deadline" #after_content>
                        <p class="subheading">{{ $t('label.application_deadline') }}:</p>
                        <p>
                            <dynamic-date-range :end="props.posting.application_deadline" time/>
                        </p>
                    </template>
                </job-page>
            </column>
            <column v-if="props.posting.similarJobs?.length || (props.posting.image && $device.isDesktop)" cols="12" lg="4">
                <sticky-content>
                    <card v-if="props.posting.image && $device.isDesktop" xs-tile sm-tile>
                        <card-image :src="props.posting.image"
                                    :alt="props.posting.title"
                                    :size="{width: 400, ratio: 3/2}" fit/>
                    </card>

                    <card v-if="props.posting.similarJobs?.length" xs-tile sm-tile>
                        <card-title spaced-content>{{ $t('label.more_jobs') }}</card-title>
                        <job-list-item v-for="similarJob in props.posting.similarJobs" :key="`job-${similarJob.id}`" :job="similarJob" small-thumbnail replace-history/>

                        <btn text large class="rounded-t-none border-t" :to="$web('jobs.index')" exact>
                            <icon :icon="icons.back"/>
                            <span>{{ $t('action.show_all') }}</span>
                        </btn>
                    </card>
                </sticky-content>
            </column>
        </row>

        <app-bottom-bar grow break-column="sm">
            <div class="flex justify-between items-center">
                <div>
                    <div v-if="![undefined, null].includes(props.posting.open_positions_count)" class="flex flex-column md:flex-row mr-24">
                        <p class="subheading mb-0 text-sm lg:text-sub">{{ $t('label.open_positions') }}:</p>
                        <p :class="props.posting.open_positions_count > 4 ? 'text-success' : 'text-warning'" class="text-sm lg:text-sub md:ml-8 mb-0">{{ Math.max(0, props.posting.open_positions_count )}}{{ props.posting.has_active_waiting_list ? ` (${$t('label.waiting_list')})` : '' }}</p>
                    </div>
                </div>

                <wrapper class="flex-grow-x-1" grow>
                    <template v-if="(authPermissions.assignment && !authPermissions.assignment.is_accepted && authPermissions.assignment.is_invitation)">
                        <form-btn :action="$api('jobs.invitations.decline', props.posting.role_id)"
                                  :data="{user_job_id: authPermissions.assignment.id}"
                                  method="PUT"
                                  color="warning"
                                  large
                                  @success="handleDeclinedInvitation"
                                  :confirm="{title: $t('confirm.job_invitation.decline.title'), message: $t('confirm.job_invitation.decline.message', {job: props.posting.title}), type: 'warning', inputs: [{type: 'text', name: 'message', placeholder: $t('confirm.job_invitation.decline.reason')}]}">
                            <icon :icon="icons.cancel"/>
                            <span class="none sm:block">{{ $t('action.decline') }}</span>
                        </form-btn>

                        <btn @click="showInvitationRespondModal = true" color="success" large>
                            <icon :icon="icons.check"/>
                            <span>{{ $t('action.accept') }}</span>
                        </btn>
                    </template>
                    <btn v-else-if="authPermissions.assignment && !authPermissions.assignment.is_accepted && !authPermissions.assignment.is_invitation" large :to="$web(`${props.group.model_name}s.show.membership`, props.group._key)"
                              color="warning" outlined>
                        <icon :icon="icons.pending"/>
                        <span>{{ $t('label.request_pending') }}</span>
                    </btn>
                    <btn v-else-if="authPermissions.assignment && authPermissions.assignment.is_accepted" color="success" large :to="$web(`${props.group.model_name}s.show.membership`, props.group._key)">
                        <span class="flex flex-column">
                            <span class="text-sm">{{ $t('label.accepted') }}</span>
                            <span class="text-3xs text-left">{{ $t('action.show_operation_details') }}</span>
                        </span>
                        <icon :icon="icons.next"/>
                    </btn>
                    <btn v-else large @click="openApplyModal" color="success">
                        <span>{{ props.posting.application_label?.title ?? $t('action.apply') }}</span>
                    </btn>
                </wrapper>
            </div>
        </app-bottom-bar>

		<client-only>
			<modal v-model="showApplyFailureModal" :title="`${props.posting.application_label?.title ?? $t('action.apply')}: ${props.posting.title}`">
				<alert type="warning" :content="authPermissions.application_failure ?? $t('error.403.message')" no-spacing/>
			</modal>

			<lazy-job-application-form-modal v-if="showApplyModal" v-model="showApplyModal" :posting="props.posting" :group="props.group" @success="handleSentApplication"/>
			<lazy-job-invitation-accept-form-modal v-if="showInvitationRespondModal" v-model="showInvitationRespondModal" :job="props.posting"
												   :group="props.group"
												   :title="`${$t('confirm.job_invitation.accept.title')}: ${props.posting.title}`"
												   :description="$t('confirm.job_invitation.accept.message', {job: props.posting.title})"
												   :invitation="authPermissions.assignment"
												   @success="handleAcceptedInvitation"/>
		</client-only>

    </div>
</template>

<script setup lang="ts">

import {mdiClockOutline} from '@mdi/js';
import {type ClubPreview, type EventPreview, ModelType} from "@spoferan/spoferan-ts-core";
import {checkIcon, nextIcon, backIcon} from "@spoferan/nuxt-spoferan/icons";
import {cancelIcon} from "@spoferan/nuxt-spoferan/icons";

const props = defineProps<{
	posting: object,

	// The group the job posting belongs to.
	group: ClubPreview | EventPreview,

	isSubPage?: boolean
}>();

const {authPermissions, refreshAuthPermissions} = useModelAuthPermissions();

const {$analytics, $web, $auth} = useNuxtApp();
const router = useRouter();

const showApplyModal = ref(false);
const showApplyFailureModal = ref(false);
const showInvitationRespondModal = ref(false);

const icons = {
	pending: mdiClockOutline,
	next: nextIcon,
	back: backIcon,
	check: checkIcon,
	cancel: cancelIcon,
};

function openApplyModal() {
	if (!authPermissions.value.can_apply) {
		showApplyFailureModal.value = true;
	} else {
		showApplyModal.value = true;
	}
}

function handleDeclinedInvitation() {
	refreshAuthPermissions(ModelType.JobPosting, props.posting.id);
}

function handleAcceptedInvitation() {
	router.replace($web(`${props.group.model_name}s.show.membership`, props.group._key));
}

function handleSentApplication() {
	$analytics.trackEvent('apply', {
		model_type_name: 'job',
		model_key: `${props.group._key}_${props.posting._key}`
	});

	router.replace($web(`${props.group.model_name}s.show.membership`, props.group._key));
}

// Refresh permissions on user change as required when user gets authenticated on clients side after SSR hydration
watch(() => $auth.user, () => {
	refreshAuthPermissions(ModelType.JobPosting, props.posting.id);
}, {
	immediate: true // Required for CSR
});
</script>